import { useEffect, useRef } from "react";
import { useApolloClient } from "@apollo/client/react";

import { NOTIFICATIONS_QUERY } from "../constants/graphql";
import { useBanner } from "../contexts/banner";
import useNotification from "./notification";
import {
  canPollBanner,
  matchesGeneratedFileBanner,
  notificationQueryVariablesForBanner,
} from "../utils/notificationMatching";

const REPORT_POLL_INTERVAL_MS = 20000;
const REPORT_BANNER_MAX_AGE_MS = 15 * 60 * 1000;

// Fallback for a missed websocket message: while a "Generating file..." banner
// is open, poll for the finished notification and hand it to `onCompleted`.
const useGeneratedFilePolling = ({ skip, onCompleted }) => {
  const apolloClient = useApolloClient();
  const { success } = useNotification();
  const { banner, closeBanner } = useBanner();

  // Kept in a ref so route changes (which change onCompleted) don't restart the interval.
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  useEffect(() => {
    if (skip || !banner.open || !canPollBanner(banner)) return undefined;

    let cancelled = false;

    const checkForCompletedFile = async () => {
      if (banner.startedAt && Date.now() - banner.startedAt > REPORT_BANNER_MAX_AGE_MS) {
        closeBanner();
        success({
          hide: 10000,
          message: "File is still generating. Check Notifications for the completed file.",
        });
        return;
      }

      let data;

      try {
        const result = await apolloClient.query({
          query: NOTIFICATIONS_QUERY,
          variables: notificationQueryVariablesForBanner(banner),
          fetchPolicy: "network-only",
        });
        data = result.data;
      } catch (e) {
        return;
      }

      if (cancelled) return;

      const completedFile = (data?.notifications || []).find(notification =>
        matchesGeneratedFileBanner(notification, banner)
      );

      if (completedFile) {
        onCompletedRef.current(completedFile, banner.type);
      }
    };

    const intervalId = window.setInterval(checkForCompletedFile, REPORT_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [apolloClient, banner, closeBanner, skip, success]);
};

export default useGeneratedFilePolling;
